import type {
  PickupCacheSchedule,
  PickupOpportunity,
  PickupOpportunityAttempt,
  PickupPipelineData,
  PickupPipelineUser
} from '@/types/pickupOpportunity';
import { pickupPointsToDollars } from '@/components/reports/pickupPricingMath';

export type PickupPipelineFilter =
  | 'open'
  | 'awaiting_answer'
  | 'needs_request'
  | 'verification'
  | 'resolved';

export type PickupPipelineWorkState = PickupPipelineFilter;

export interface PickupPipelineRow {
  opportunity: PickupOpportunity;
  attempts: PickupOpportunityAttempt[];
  activeAttempt: PickupOpportunityAttempt | null;
  displayAttempt: PickupOpportunityAttempt | null;
  ownerName: string;
  candidatePosition: number;
  candidateCount: number;
  nextPricingAt: string | null;
  cacheSchedule: PickupCacheSchedule | null;
}

export interface PickupPipelineSummary {
  actionRequired: number;
  renegotiationsPending: number;
  guidelinesBeingChecked: number;
  switchesInProgress: number;
  openPotentialDollars: number;
  capturedThisMonthDollars: number;
  verifiedThisMonthDollars: number;
}

export type PickupLadderRungState = 'open' | 'answered' | 'queued' | 'stale';

export interface PickupLadderEconomics {
  activePotentialDollars: number | null;
  nextPotentialDollars: number | null;
  activeInvestor: string | null;
}

export type PickupPipelineFilterCounts = Record<PickupPipelineFilter, number>;

const ACTION_REQUIRED_STATUSES = new Set([
  'renegotiation_required',
  'checking_guidelines',
  'guidelines_passed',
  'switching_investor',
  'awaiting_lock_upload'
]);

const SWITCH_STATUSES = new Set([
  'guidelines_passed',
  'switching_investor',
  'awaiting_lock_upload'
]);

const ANSWERED_ATTEMPT_STATUSES = new Set([
  'approved',
  'declined',
  'transferred',
  'cannot_transfer',
  'skipped'
]);

export function pickupAttemptPoints(attempt: PickupOpportunityAttempt | null): number | null {
  return attempt?.latest_observed_pickup_points
    ?? attempt?.quoted_pickup_points
    ?? null;
}

export function pickupAttemptPotentialDollars(
  attempt: PickupOpportunityAttempt | null,
  loanAmount: number | null
): number | null {
  return pickupPointsToDollars(pickupAttemptPoints(attempt), loanAmount);
}

export function pickupLadderRungState(
  attempt: PickupOpportunityAttempt,
  activeAttemptId: string | null
): PickupLadderRungState {
  if (attempt.id === activeAttemptId && attempt.is_active) {
    return 'open';
  }
  if (attempt.status === 'stale') {
    return 'stale';
  }
  if (ANSWERED_ATTEMPT_STATUSES.has(attempt.status)) {
    return 'answered';
  }
  return 'queued';
}

export function pickupLadderOpenLabel(attempt: PickupOpportunityAttempt): string {
  if (attempt.status === 'renegotiation_required' || attempt.status === 'checking_guidelines') {
    return 'Next — not yet contacted';
  }
  if (attempt.status === 'pending_investor_response') {
    return 'Awaiting response';
  }
  return pickupAttemptStatusLabel(attempt.status);
}

export function pickupVerificationRequestBlock(row: PickupPipelineRow): string | null {
  const status = row.cacheSchedule?.pickup_verification_status;
  if (status === 'verified' || status === 'verified_with_drift') {
    return null;
  }
  if (status === 'waiting_for_ob_sync') {
    return 'Request locked while the OB lock is still syncing.';
  }
  if (status === 'ob_mismatch') {
    return 'Request locked until the Loan Center and OB lock identities match.';
  }
  if (status === 'ob_access_error') {
    return 'Request locked until OB access recovers.';
  }
  return 'Request locked until this loan is verified against OB.';
}

export function pickupLadderEconomics(row: PickupPipelineRow): PickupLadderEconomics {
  const next = row.attempts.find((attempt) =>
    attempt.rank > (row.activeAttempt?.rank || 0)
    && attempt.status === 'queued'
  ) || null;
  return {
    activePotentialDollars: pickupAttemptPotentialDollars(
      row.activeAttempt,
      row.opportunity.loan_amount
    ),
    nextPotentialDollars: pickupAttemptPotentialDollars(next, row.opportunity.loan_amount),
    activeInvestor: row.activeAttempt?.investor_name || null
  };
}

export function pickupUserName(user: PickupPipelineUser | undefined): string {
  if (!user) {
    return 'Unassigned';
  }
  return user.full_name?.trim()
    || [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
    || user.email;
}

export function buildPickupPipelineRows(data: PickupPipelineData): PickupPipelineRow[] {
  const attemptsByOpportunity = new Map<string, PickupOpportunityAttempt[]>();
  for (const attempt of data.attempts) {
    const attempts = attemptsByOpportunity.get(attempt.opportunity_id) || [];
    attempts.push(attempt);
    attemptsByOpportunity.set(attempt.opportunity_id, attempts);
  }
  for (const attempts of attemptsByOpportunity.values()) {
    attempts.sort((left, right) => left.rank - right.rank || left.id.localeCompare(right.id));
  }
  const userById = new Map(data.users.map((user) => [user.id, user]));
  const scheduleByCacheId = new Map<string, PickupCacheSchedule>(
    data.cacheSchedules.map((schedule) => [schedule.id, schedule])
  );

  return data.opportunities.map((opportunity) => {
    const attempts = attemptsByOpportunity.get(opportunity.id) || [];
    const activeAttempt = attempts.find((attempt) => attempt.id === opportunity.active_attempt_id)
      || attempts.find((attempt) => attempt.is_active)
      || null;
    const displayAttempt = activeAttempt
      || attempts.find((attempt) => attempt.id === opportunity.terminal_attempt_id)
      || attempts[attempts.length - 1]
      || null;
    const candidatePosition = displayAttempt
      ? Math.max(1, attempts.findIndex((attempt) => attempt.id === displayAttempt.id) + 1)
      : 0;
    const cacheSchedule = opportunity.ob_loan_cache_id
      ? scheduleByCacheId.get(opportunity.ob_loan_cache_id) || null
      : null;
    return {
      opportunity,
      attempts,
      activeAttempt,
      displayAttempt,
      ownerName: pickupUserName(opportunity.owner_id ? userById.get(opportunity.owner_id) : undefined),
      candidatePosition,
      candidateCount: attempts.length,
      nextPricingAt: cacheSchedule?.pickup_next_pricing_eligible_at ?? null,
      cacheSchedule
    };
  }).sort(comparePickupUrgency);
}

export function comparePickupUrgency(left: PickupPipelineRow, right: PickupPipelineRow): number {
  const leftExpiration = Date.parse(left.opportunity.latest_lock_expiration_at || '');
  const rightExpiration = Date.parse(right.opportunity.latest_lock_expiration_at || '');
  const leftValue = Number.isFinite(leftExpiration) ? leftExpiration : Number.POSITIVE_INFINITY;
  const rightValue = Number.isFinite(rightExpiration) ? rightExpiration : Number.POSITIVE_INFINITY;
  return leftValue - rightValue || left.opportunity.id.localeCompare(right.opportunity.id);
}

export function filterPickupPipelineRows(
  rows: PickupPipelineRow[],
  filter: PickupPipelineFilter
): PickupPipelineRow[] {
  return rows.filter((row) => pickupPipelineWorkState(row) === filter);
}

export function countPickupPipelineFilters(
  rows: PickupPipelineRow[]
): PickupPipelineFilterCounts {
  const counts: PickupPipelineFilterCounts = {
    open: 0,
    awaiting_answer: 0,
    needs_request: 0,
    verification: 0,
    resolved: 0
  };
  for (const row of rows) {
    counts[pickupPipelineWorkState(row)] += 1;
  }
  return counts;
}

export function pickupPipelineWorkState(row: PickupPipelineRow): PickupPipelineWorkState {
  if (row.opportunity.status !== 'open') {
    return 'resolved';
  }
  if (pickupVerificationRequestBlock(row)) {
    return 'verification';
  }
  if (row.activeAttempt?.status === 'pending_investor_response') {
    return 'awaiting_answer';
  }
  if (row.activeAttempt?.status === 'renegotiation_required'
    || row.activeAttempt?.status === 'checking_guidelines') {
    return 'needs_request';
  }
  return 'open';
}

export function pickupPipelineWorkStateLabel(state: PickupPipelineWorkState): string {
  const labels: Record<PickupPipelineWorkState, string> = {
    open: 'Open',
    awaiting_answer: 'Awaiting answer',
    needs_request: 'Needs request',
    verification: 'Verification',
    resolved: 'Resolved'
  };
  return labels[state];
}

function isSameMonth(value: string | null, nowMs: number): boolean {
  if (!value) {
    return false;
  }
  const date = new Date(value);
  const now = new Date(nowMs);
  return !Number.isNaN(date.getTime())
    && date.getFullYear() === now.getFullYear()
    && date.getMonth() === now.getMonth();
}

export function summarizePickupPipeline(
  rows: PickupPipelineRow[],
  nowMs = Date.now()
): PickupPipelineSummary {
  const open = rows.filter((row) => row.opportunity.status === 'open');
  const capturedThisMonth = rows.filter((row) =>
    row.opportunity.status !== 'open'
    && isSameMonth(row.opportunity.captured_claimed_at, nowMs)
  );
  return {
    actionRequired: open.filter((row) => ACTION_REQUIRED_STATUSES.has(row.activeAttempt?.status || '')).length,
    renegotiationsPending: open.filter((row) => row.activeAttempt?.status === 'pending_investor_response').length,
    guidelinesBeingChecked: open.filter((row) => row.activeAttempt?.status === 'checking_guidelines').length,
    switchesInProgress: open.filter((row) => SWITCH_STATUSES.has(row.activeAttempt?.status || '')).length,
    openPotentialDollars: open.reduce(
      (sum, row) => sum + Math.max(0, pickupPointsToDollars(
        row.opportunity.latest_potential_points,
        row.opportunity.loan_amount
      ) || 0),
      0
    ),
    capturedThisMonthDollars: capturedThisMonth.reduce(
      (sum, row) => sum + (Number(row.opportunity.net_captured_dollars) || 0),
      0
    ),
    verifiedThisMonthDollars: capturedThisMonth.reduce(
      (sum, row) => sum + (row.opportunity.verified_at
        ? Number(row.opportunity.net_captured_dollars) || 0
        : 0),
      0
    )
  };
}

export function pickupAttemptStatusLabel(status: string | null | undefined): string {
  const labels: Record<string, string> = {
    queued: 'Queued',
    renegotiation_required: 'Renegotiation Required',
    pending_investor_response: 'Pending Investor Response',
    approved: 'Approved',
    declined: 'Declined',
    checking_guidelines: 'Checking Guidelines',
    guidelines_passed: 'Guidelines Passed',
    switching_investor: 'Switching Investor',
    awaiting_lock_upload: 'Awaiting Lock Upload',
    transferred: 'Transferred',
    cannot_transfer: 'Cannot Transfer',
    skipped: 'Skipped',
    stale: 'Stale',
    assign_owner: 'Assign Owner',
    start_renegotiation: 'Start Renegotiation',
    approve_renegotiation: 'Approve Renegotiation',
    decline_renegotiation: 'Decline Renegotiation',
    pass_guidelines: 'Pass Guidelines',
    start_investor_switch: 'Start Investor Switch',
    await_lock_upload: 'Await Lock Upload',
    complete_transfer: 'Complete Transfer',
    skip_candidate: 'Skip Candidate'
  };
  return status ? labels[status] || status : 'Resolved';
}

export function pickupMasterStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    open: 'Open',
    captured_renegotiated: 'Captured — Renegotiated',
    captured_investor_switched: 'Captured — Investor Switched',
    not_captured_all_candidates_exhausted: 'Not Captured — All Candidates Exhausted',
    closed_loan_no_longer_eligible: 'Closed — Loan No Longer Eligible'
  };
  return labels[status] || status;
}
